import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  Status,
  ThreeDotMenu,
  ThreeDotMenuItem,
  Tooltip,
} from '@mm/components';
import { useMailchimpConnectionCRUDActions } from '@mm/state/actions/MailchimpConnectionsCRUDActions';
import { useMailchimpCRUDActions } from '@mm/state/actions/MailchimpCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { FormattedConnectionModel } from '@mm/state/model/ConnectionModel';
import { MAX_PAGE_SIZE } from '@mm/state/model/PageModel';
import {
  MAILCHIMP,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import { activeIntegrationSelector } from '@mm/state/modules/connectionsTableState/selectors';
import {
  orderedMailchimpConnectionsSelector,
  selectMailchimpConnections,
} from '@mm/state/modules/mailchimpConnectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectMailchimpAccounts } from '@mm/state/selectors/mailchimpSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { colors } from '@mm/utils';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IntegrationErrors } from '../../../constants';
import { IntegrationMailchimpComponent } from './IntegrationMailchimpComponent';

type CellProps = { item: FormattedConnectionModel };

const renderErrorIcon = item => {
  const haveError =
    item.webhookErrors?.length &&
    item.webhookErrors
      .map(err => err.errorDetails)
      .some(fullError =>
        IntegrationErrors.some(errorSubstring =>
          fullError.toLowerCase().includes(errorSubstring),
        ),
      );

  if (haveError) {
    return (
      <Tooltip
        title=" Please check your Mailchimp Account. Make sure that access is
        granted(not expired) and merge fields are configured correctly."
      >
        <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
      </Tooltip>
    );
  }
};

const renderNameCell = (
  item: FormattedConnectionModel,
  editMailchimp: (id: number) => void,
) => (
  <div
    className="connection-table__clickable-cell"
    onClick={() => editMailchimp(item.id)}
  >
    {item.name} {renderErrorIcon(item)}
  </div>
);

const renderStatusCell = ({ item }: CellProps) => {
  const { statusText, active } = item;
  return (
    <Status
      status={statusText}
      color={!active ? colors.grayLight : undefined}
    />
  );
};

const renderActionsCell = (
  item: FormattedConnectionModel,
  editMailchimp: (id: number) => void,
  deleteMailchimp: (item: FormattedConnectionModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return deleteMailchimp(item);
      default:
        break;
    }
  };

  const elements: ThreeDotMenuItem[] = [
    {
      name: 'Delete',
      value: 'delete',
      children: (
        <div className="connection-table__action-cell-item">
          <div className="connection-table__action-cell-item_icon">
            <DeleteOutlined />
          </div>
          <div>Delete</div>
        </div>
      ),
      hideName: true,
    },
  ];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="connection-table__action-cell">
      <EditOutlined
        onClick={() => editMailchimp(item.id)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </div>
  );
};

export const generateTableColumns = (
  editMailchimp: (id: number) => void,
  deleteMailchimp: (item: FormattedConnectionModel) => void,
) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.name.localeCompare(b.name),
      render: (text, record: FormattedConnectionModel) =>
        renderNameCell(record, editMailchimp),
      width: '45%',
    },
    {
      title: 'Leads sent',
      dataIndex: 'contactsSent',
      key: 'contactsSent',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        (a.contactsSent || 0) - (b.contactsSent || 0),
      render: (text, record) => record.contactsSent,
      width: '15%',
    },
    {
      title: 'Status',
      dataIndex: 'statusText',
      key: 'statusText',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        a.statusText.localeCompare(b.statusText),
      render: (text, record: FormattedConnectionModel) =>
        renderStatusCell({ item: record }),
      width: '15%',
    },
    {
      title: 'Created',
      dataIndex: 'formattedCreatedAt',
      key: 'formattedCreatedAt',
      sorter: (a: FormattedConnectionModel, b: FormattedConnectionModel) =>
        new Date(a.formattedCreatedAt).getTime() -
        new Date(b.formattedUpdatedAt).getTime(),
      render: (text, record) => record.formattedCreatedAt,
      width: '15%',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, record: FormattedConnectionModel) =>
        renderActionsCell(record, editMailchimp, deleteMailchimp),
      width: '10%',
    },
  ];
  return columns;
};

export const MailchimpIntegration = () => {
  const { setActiveIntegration } = useConnectionsTableActions();
  const activeIntegration = useSelector(activeIntegrationSelector);
  const accounts = useSelector(selectMailchimpAccounts);
  const { getAccounts, disconnectAccount } = useMailchimpCRUDActions();
  const { retrieveAll } = useMailchimpConnectionCRUDActions();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const connections = useSelector(orderedMailchimpConnectionsSelector);
  const mailchimpConnections = useSelector(selectMailchimpConnections);
  const { deleteConnection } = useMailchimpConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal, openGeneralConfirmModal } = useModalActions();

  useEffect(() => {
    getAccounts(facebookPageId);
    retrieveAll(facebookPageId, {
      type: 'mailchimp',
      pageSize: MAX_PAGE_SIZE,
    });
  }, []);

  const onDisconnect = (id: number) => {
    const accountEmail = accounts?.find(acc => acc.id === id)?.email;
    openGeneralConfirmModal({
      headerText: `Are you sure you want to disconnect ${accountEmail} account from Mailchimp Integration?`,
      messageText: '',
      onCloseHandler: ok => {
        if (ok) {
          disconnectAccount(id, facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      okButtonStyling: { red: true },
      cancelButtonText: 'Cancel',
      cancelButtonFirst: true,
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };
  const deleteMailchimp = (item: FormattedConnectionModel) => {
    const deleteCallback = async () => {
      deleteConnection(facebookPageId, item);
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${item.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };
  const setActiveIntegrationHandler = () => {
    if (activeIntegration === MAILCHIMP) return;
    setActiveIntegration(MAILCHIMP);
  };

  const editMailchimp = (id: number) => {
    setActiveIntegrationHandler();
    showSlideoutMenu('mailchimpIntegrationEditor', {
      widgetId: id,
    });
  };

  const columns = generateTableColumns(editMailchimp, deleteMailchimp);

  return (
    <IntegrationMailchimpComponent
      isAccountConnected={!!accounts.length}
      mailchimpCount={connections.length}
      onDisconnect={onDisconnect}
      columns={columns}
      mailchimpConnections={mailchimpConnections}
      name={MAILCHIMP}
    />
  );
};
